import React, { useState } from 'react';
import { setAnalyticsIsEnabled, setIsPaying } from 'services/profiles';
import {
    Button,
    ConfirmButton,
    COUNTRY_CODES,
    DateDistanceDisplay,
    LoadingIndicator,
} from '@ygtechnologies/bygr-frontend-component-library';
import { BygrProfileDetails, ProfileType } from 'types';
import PrimaryCard from 'components/Statistics/PrimaryCard';
import theme from './Profile.module.scss';
import { BooleanDisplay } from 'components/Generic';
import EditProfileModal from './EditProfileModal';
import { faPencil } from '@fortawesome/pro-light-svg-icons';

interface Props {
    profile: BygrProfileDetails;
    reloadProfile(): void;
}

const ProfileDashboard: React.FC<Props> = ({ profile, reloadProfile }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isEditProfileModalOpen, setIsEditProfileModalOpen] = useState(false);
    const handleChangeIsPaying = async () => {
        setIsLoading(true);
        await setIsPaying(profile!.id, !profile!.isPaying);
        setIsLoading(false);
        reloadProfile();
    };

    const handleChangeAnalytics = async () => {
        setIsLoading(true);
        await setAnalyticsIsEnabled(profile!.id, !profile!.analyticsIsEnabled);
        setIsLoading(false);
        reloadProfile();
    };

    if (!profile) return <LoadingIndicator />;

    return (
        <div className={theme.dashboard}>
            <PrimaryCard title={profile.name}>
                <PrimaryCard.Row label="Number of members">{profile.members.length}</PrimaryCard.Row>

                {profile.type === ProfileType.Seller && (
                    <>
                        <PrimaryCard.Row label="Number of units">{profile.numberOfUnitsTotal}</PrimaryCard.Row>
                        <PrimaryCard.Row label="Number of projects">{profile.projects.length}</PrimaryCard.Row>
                        <PrimaryCard.Row label="Unit of measurement">{profile.defaultUnitOfMeasurement}</PrimaryCard.Row>
                        <PrimaryCard.Row label="Country">
                            {COUNTRY_CODES[profile.countryCode]}
                            <Button
                                className={theme.editIconButton}
                                color="tertiary"
                                size="xsmall"
                                onClick={() => setIsEditProfileModalOpen(true)}
                                icon={faPencil}
                            />
                        </PrimaryCard.Row>
                        <PrimaryCard.Row label="Currency">
                            {profile.currency ? (
                                <>
                                    {profile.currency.name} ({profile.currency.code})
                                </>
                            ) : (
                                <>N/A</>
                            )}
                            <Button
                                className={theme.editIconButton}
                                color="tertiary"
                                size="xsmall"
                                onClick={() => setIsEditProfileModalOpen(true)}
                                icon={faPencil}
                            />
                        </PrimaryCard.Row>

                        <PrimaryCard.Row label="Is paying">
                            <BooleanDisplay value={profile.isPaying} />
                            &nbsp;
                            <ConfirmButton
                                size="xxsmall"
                                loading={isLoading}
                                dialogTitle="Set is paying for profile"
                                dialogBody={`Are you sure you want to set is paying to ${!profile.isPaying}`}
                                onConfirm={handleChangeIsPaying}
                                color="tertiary">
                                Toggle
                            </ConfirmButton>
                        </PrimaryCard.Row>
                        <PrimaryCard.Row label="Analytics enabled">
                            <BooleanDisplay value={profile.analyticsIsEnabled} />
                            &nbsp;
                            <ConfirmButton
                                size="xxsmall"
                                loading={isLoading}
                                dialogTitle="Set analytics for profile"
                                dialogBody={`Are you sure you want to set analytics to ${!profile.analyticsIsEnabled}`}
                                onConfirm={handleChangeAnalytics}
                                color="tertiary">
                                Toggle
                            </ConfirmButton>
                        </PrimaryCard.Row>
                    </>
                )}
                <PrimaryCard.Row label="Create date">
                    {profile.createDate && <DateDistanceDisplay value={profile.createDate} />}
                </PrimaryCard.Row>
            </PrimaryCard>
            <EditProfileModal
                profile={profile}
                isOpen={isEditProfileModalOpen}
                toggle={() => setIsEditProfileModalOpen(false)}
                onSave={reloadProfile}
            />
        </div>
    );
};

export default ProfileDashboard;
